import { useState } from "react";
import { brands, Brand } from "@/mocks/brands";
import { products, Product } from "@/mocks/products";
import ProductDetailModal from "@/components/base/ProductDetailModal";

/* ── 브랜드 편집 모달 ── */
interface EditBrandModalProps {
  brand: Brand;
  onSave: (updated: Brand) => void;
  onClose: () => void;
}

const EditBrandModal = ({ brand, onSave, onClose }: EditBrandModalProps) => {
  const [draft, setDraft] = useState<Brand>({ ...brand });
  const [logoErr, setLogoErr] = useState(false);

  const toggle = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      productIds: prev.productIds.includes(id)
        ? prev.productIds.filter((p) => p !== id)
        : [...prev.productIds, id],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-stone-100">
          <div>
            <h3 className="text-sm font-black text-stone-900">{brand.name} 브랜드 수정</h3>
            <p className="text-xs text-stone-400 mt-0.5">브랜드 정보와 제품 목록을 변경할 수 있어요</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* 브랜드명 */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">브랜드명</label>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400" />
          </div>

          {/* 로고 URL */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">로고 이미지 URL</label>
            <input value={draft.logo} onChange={(e) => { setDraft({ ...draft, logo: e.target.value }); setLogoErr(false); }}
              placeholder="https://... 로고 이미지 주소"
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400" />
            {draft.logo && (
              <div className="mt-2 h-14 bg-stone-50 rounded-lg flex items-center justify-center overflow-hidden border border-stone-100">
                {!logoErr ? (
                  <img src={draft.logo} alt="로고 미리보기" className="h-10 object-contain" onError={() => setLogoErr(true)} />
                ) : (
                  <p className="text-xs text-stone-400">이미지를 불러올 수 없어요</p>
                )}
              </div>
            )}
          </div>

          {/* 소개 */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">브랜드 소개</label>
            <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400" />
          </div>

          {/* 제조국 */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">제조국</label>
            <input value={draft.origin} onChange={(e) => setDraft({ ...draft, origin: e.target.value })}
              className="w-full border border-stone-200 rounded-lg px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400" />
          </div>

          {/* 제품 선택 */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">포함 제품 선택</label>
            <div className="space-y-2">
              {products.map((p) => (
                <label key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-50 cursor-pointer">
                  <input type="checkbox" checked={draft.productIds.includes(p.id)} onChange={() => toggle(p.id)}
                    className="w-4 h-4 accent-stone-900 cursor-pointer" />
                  <img src={p.image} alt={p.name} className="w-8 h-8 rounded-lg object-cover object-top flex-shrink-0" />
                  <span className="text-sm text-stone-700 font-medium">{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-stone-200 text-stone-600 text-sm font-semibold rounded-xl hover:bg-stone-50 cursor-pointer whitespace-nowrap">취소</button>
          <button onClick={() => { onSave(draft); onClose(); }} className="flex-1 py-2.5 bg-stone-900 text-white text-sm font-bold rounded-xl hover:bg-stone-700 cursor-pointer whitespace-nowrap">저장하기</button>
        </div>
      </div>
    </div>
  );
};

/* ── 브랜드 섹션 ── */
const BrandSection = () => {
  const [brandList, setBrandList] = useState<Brand[]>(brands);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeBrand, setActiveBrand] = useState<string>(brands[0]?.id ?? "");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const editingBrand = brandList.find((b) => b.id === editingId) ?? null;
  const currentBrand = brandList.find((b) => b.id === activeBrand);
  const brandProducts = currentBrand
    ? products.filter((p) => currentBrand.productIds.includes(p.id))
    : [];

  const handleSave = (updated: Brand) => {
    setBrandList((prev) => prev.map((b) => b.id === updated.id ? updated : b));
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-1">Brands</p>
          <h2 className="text-2xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            브랜드별 제품
          </h2>
        </div>
      </div>

      {/* Brand Tabs */}
      <div className="flex flex-wrap gap-3 mb-8">
        {brandList.map((brand) => (
          <div key={brand.id} className="relative group">
            <button
              onClick={() => setActiveBrand(brand.id)}
              className={`flex items-center gap-3 px-5 py-3 rounded-2xl border transition-all cursor-pointer ${
                activeBrand === brand.id
                  ? "border-stone-900 bg-stone-900"
                  : "border-stone-200 bg-white hover:border-stone-400"
              }`}
            >
              {/* Logo */}
              <div className={`w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 ${activeBrand === brand.id ? "bg-white/10" : "bg-stone-50"}`}>
                <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain p-1"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
              </div>
              <div className="text-left">
                <p className={`text-sm font-black ${activeBrand === brand.id ? "text-white" : "text-stone-900"}`}>{brand.name}</p>
                <p className={`text-[10px] ${activeBrand === brand.id ? "text-white/60" : "text-stone-400"}`}>{brand.productIds.length}개 제품 · {brand.origin}</p>
              </div>
            </button>
            {/* Edit button */}
            <button
              onClick={() => setEditingId(brand.id)}
              className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-white border border-stone-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-stone-50"
            >
              <i className="ri-edit-line text-stone-500 text-[10px]" />
            </button>
          </div>
        ))}
      </div>

      {/* Brand Info Bar */}
      {currentBrand && (
        <div className="flex items-center gap-4 bg-stone-50 rounded-2xl px-5 py-4 mb-6">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-white flex items-center justify-center flex-shrink-0 border border-stone-100">
            <img src={currentBrand.logo} alt={currentBrand.name} className="w-full h-full object-contain p-1"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-black text-stone-900">{currentBrand.name}</p>
            <p className="text-xs text-stone-500 mt-0.5">{currentBrand.description}</p>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-3 py-1.5">
            <i className="ri-map-pin-line text-stone-400 text-xs" />
            <span className="text-xs font-semibold text-stone-600">{currentBrand.origin}</span>
          </div>
          <button
            onClick={() => setEditingId(currentBrand.id)}
            className="flex items-center gap-1.5 border border-stone-200 text-stone-600 text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-xs" />
            브랜드 수정
          </button>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
        {brandProducts.map((product) => (
          <div
            key={product.id}
            className="group bg-white rounded-xl border border-stone-100 hover:border-stone-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            onClick={() => setSelectedProduct(product)}
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: "3/4" }}>
              <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              {product.badge && (
                <span className={`absolute top-2.5 left-2.5 text-[10px] font-bold px-2.5 py-1 rounded-full ${product.badge === "BEST" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {product.badge}
                </span>
              )}
            </div>
            <div className="p-4">
              <p className="text-[10px] font-bold text-stone-400 tracking-widest uppercase mb-1">{product.category}</p>
              <h3 className="text-sm font-bold text-stone-900 mb-2 leading-snug">{product.name}</h3>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((s) => (
                  <div key={s} className="w-3 h-3 flex items-center justify-center">
                    <i className={`text-[10px] ${s <= Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`} />
                  </div>
                ))}
                <span className="text-xs text-stone-400 ml-1">({product.reviews})</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingBrand && (
        <EditBrandModal brand={editingBrand} onSave={handleSave} onClose={() => setEditingId(null)} />
      )}
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
};

export default BrandSection;
